import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  Crown,
  ShieldCheck,
  Wallet,
  Briefcase,
  Award,
  ChevronDown,
  ArrowUpRight,
  User,
  Search,
} from "lucide-react";
import TeamHeader from "@/components/TeamHeader";
import { Reveal } from "@/components/Reveal";
import { TEAM } from "@/data/site";

export default function Team() {
  const [searchQuery, setSearchQuery] = useState("");

  // Dynamically segment and filter team members into org chart buckets
  const {
    president,
    secretariat,
    deputies,
    financeTeam,
    ceo,
    ceoTeam,
    advisors,
    hasResults,
  } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = TEAM.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.role.toLowerCase().includes(query) ||
        (m.dept && m.dept.toLowerCase().includes(query))
    );

    const president = filtered.find(
      (m) =>
        m.role.toLowerCase().includes("president") &&
        !m.role.toLowerCase().includes("deputy") &&
        !m.role.toLowerCase().includes("vice")
    );

    const secretariat = filtered.filter(
      (m) =>
        m.role.toLowerCase().includes("secretary") ||
        m.role.toLowerCase().includes("secretariat") ||
        m.role.toLowerCase().includes("auditor")
    );

    const deputies = filtered.filter(
      (m) =>
        m.role.toLowerCase().includes("deputy") ||
        m.role.toLowerCase().includes("vice")
    );

    const financeTeam = filtered.filter((m) =>
      m.role.toLowerCase().includes("treasurer")
    );

    const ceo = filtered.find(
      (m) =>
        m.role.toLowerCase().includes("ceo") ||
        m.role.toLowerCase().includes("chief executive")
    );

    const ceoTeam = filtered.filter(
      (m) =>
        (
          m.role.toLowerCase().includes("auditor") ||
          m.role.toLowerCase().includes("operations") ||
          m.role.toLowerCase().includes("manager")) &&
        !m.role.toLowerCase().includes("ceo")
    );

    const advisors = filtered.filter((m) =>
      m.role.toLowerCase().includes("advisor")
    );

    const hasResults =
      Boolean(president) ||
      secretariat.length > 0 ||
      deputies.length > 0 ||
      financeTeam.length > 0 ||
      Boolean(ceo) ||
      ceoTeam.length > 0 ||
      advisors.length > 0;

    return {
      president,
      secretariat,
      deputies,
      financeTeam,
      ceo,
      ceoTeam,
      advisors,
      hasResults,
    };
  }, [searchQuery]);

  return (
    <div data-testid="team-page" className="bg-slate-50/50 min-h-screen">
      <TeamHeader />

      {/* Main Container */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-28 md:pb-36 border-b border-slate-200 overflow-hidden">
        {/* Section Header */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-michma-soft px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-michma font-extrabold mb-4 border border-michma/20">
              <Layers className="w-4 h-4" /> The Board of Council
            </span>
            <h2 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight text-michma-ink">
              Organizational Hierarchy
            </h2>
            <p className="mt-4 text-slate-700 text-xl leading-relaxed">
              Guided by a shared commitment to our mission, the Board of Council
              acts as the chief stewards of our organization. This dedicated
              group of community leaders and advocates works collaboratively to
              set policy, ensure financial health, and amplify our impact.
            </p>
          </div>
        </Reveal>

        {/* Search Control */}
        <div className="mb-16 max-w-md mx-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, role, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-slate-200/80 text-sm shadow-xs focus:outline-none focus:border-michma focus:ring-1 focus:ring-michma transition-all"
            />
          </div>
        </div>

        {/* Dynamic Org Chart */}
        {hasResults ? (
          <div className="relative max-w-[1400px] mx-auto">
            {/* ===================== TIER 1: PRESIDENT ===================== */}
            {president && (
              <div className="relative z-10 mb-2">
                <Reveal>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Crown className="w-6 h-6 text-amber-500" />
                    <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold">
                      President
                    </span>
                  </div>

                  {/* President Card */}
                  <div className="max-w-md mx-auto">
                    <Link
                      to={`/team/${president.slug}`}
                      data-testid={`team-member-${president.slug}`}
                      className="group block relative rounded-3xl border-2 border-michma bg-gradient-to-b from-michma-soft/50 to-white p-6 shadow-lg hover:shadow-2xl transition-all duration-300 text-center z-10"
                    >
                      <div className="relative w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-md bg-slate-100 flex items-center justify-center">
                        {president.image ? (
                          <img
                            src={president.image}
                            alt={president.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <User className="w-12 h-12 text-slate-400" />
                        )}
                        <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-michma text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma font-bold block mb-1">
                        {president.dept || "Executive Office"}
                      </span>
                      <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-michma-ink group-hover:text-michma transition-colors">
                        {president.name}
                      </h3>
                      <div className="mt-3 inline-block rounded-full bg-michma text-white px-5 py-1.5 font-mono text-xs font-extrabold uppercase tracking-wider">
                        {president.role}
                      </div>
                    </Link>
                  </div>
                </Reveal>
              </div>
            )}

            {/* ===================== TREE LINES TO TIER 2 ===================== */}
            {(deputies.length > 0 || secretariat.length > 0 || financeTeam.length > 0) && (
              <div className="relative flex flex-col items-center justify-center mt-2 z-0 w-full">
                {/* Vertical trunk dropping down from President */}
                <div className="w-[3px] h-10 bg-slate-300" />
                
                {/* Horizontal crossbar connecting the 3 columns (Desktop only) */}
                {/* 66.66% width perfectly aligns with the centers of a 3-column CSS grid */}
                <div className="hidden lg:block w-[66.66%] h-[3px] bg-slate-300" />
                
                {/* Center arrow dropping into Deputies */}
                <div className="w-10 h-10 rounded-full bg-slate-100 border-2 border-slate-300 flex items-center justify-center text-slate-600 shadow-md -mt-5 z-10 bg-white relative">
                  <ChevronDown className="w-6 h-6 animate-bounce" />
                </div>
              </div>
            )}

            {/* ===================== TIER 2: SECRETARIAT, DEPUTIES, TREASURY ===================== */}
            {/* Displayed in a 3-column grid on desktop, single column stack on mobile */}
            {(deputies.length > 0 || secretariat.length > 0 || financeTeam.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 w-full max-w-6xl mx-auto relative z-10 pt-4 pb-8">
                
                {/* LEFT COLUMN: SECRETARIAT */}
                <div className="flex flex-col order-2 lg:order-1 relative w-full">
                  {secretariat.length > 0 && (
                    <Reveal>
                      {/* Left Arrow Drop from Crossbar (Desktop only) */}
                      <div className="hidden lg:flex flex-col items-center absolute w-full -top-[40px] left-0 pointer-events-none">
                        <div className="w-[3px] h-7 bg-slate-300" />
                        <ChevronDown className="text-slate-400 w-6 h-6 -mt-2 animate-bounce" />
                      </div>

                      <div className="bg-white border-2 border-slate-200 rounded-3xl p-6 shadow-sm h-full">
                        <div className="flex items-center justify-center gap-2 mb-6 text-slate-600 font-mono text-xs font-bold uppercase tracking-widest">
                          <Briefcase className="w-4 h-4" /> Secretariat & Audit
                        </div>
                        <div className="flex flex-col gap-4">
                          {secretariat.map((sec) => (
                            <Link
                              key={sec.slug || sec.name}
                              to={`/team/${sec.slug}`}
                              data-testid={`team-member-${sec.slug}`}
                              className="group flex flex-col xl:flex-row items-center gap-4 bg-slate-50/50 border border-slate-200 rounded-2xl p-4 hover:border-michma hover:shadow-md hover:bg-white transition-all duration-300 text-center xl:text-left"
                            >
                              <div className="relative w-14 h-14 rounded-full overflow-hidden bg-white shrink-0 border border-slate-200 flex items-center justify-center">
                                {sec.image ? (
                                  <img
                                    src={sec.image}
                                    alt={sec.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                ) : (
                                  <User className="w-6 h-6 text-slate-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-heading font-extrabold text-base text-michma-ink group-hover:text-michma transition-colors">
                                  {sec.name}
                                </h4>
                                <span className="text-[11px] font-bold text-slate-500 block mt-0.5 uppercase tracking-wide">
                                  {sec.role}
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  )}
                </div>

                {/* CENTER COLUMN: VICE PRESIDENTS (DEPUTIES) */}
                <div className="flex flex-col order-1 lg:order-2 w-full">
                  {deputies.length > 0 && (
                    <Reveal>
                      <div className="flex items-center justify-center gap-2 mb-6 mt-2 lg:mt-0">
                        <ShieldCheck className="w-6 h-6 text-michma" />
                        <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold">
                          Vice Presidents
                        </span>
                      </div>
                      <div className="flex flex-col gap-5">
                        {deputies.map((member, i) => (
                          <Link
                            key={member.slug || i}
                            to={`/team/${member.slug}`}
                            data-testid={`team-member-${member.slug}`}
                            className="group relative rounded-3xl border-2 border-slate-200 bg-white p-6 shadow-xs hover:border-michma hover:shadow-xl transition-all duration-300 flex flex-col items-center text-center"
                          >
                            <div className="relative w-20 h-20 mb-3 rounded-full overflow-hidden bg-slate-100 border-2 border-slate-200 flex items-center justify-center">
                              {member.image ? (
                                <img
                                  src={member.image}
                                  alt={member.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <User className="w-8 h-8 text-slate-400" />
                              )}
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1 font-bold">
                              {member.dept || "Domain Focus"}
                            </span>
                            <h3 className="font-heading font-extrabold text-lg text-michma-ink group-hover:text-michma transition-colors">
                              {member.name}
                            </h3>
                            <p className="font-mono text-[11px] font-extrabold uppercase tracking-wider text-michma mt-3 pt-3 border-t border-slate-100 w-full">
                              {member.role}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </Reveal>
                  )}
                </div>

                {/* RIGHT COLUMN: TREASURY */}
                <div className="flex flex-col order-3 lg:order-3 relative w-full">
                  {financeTeam.length > 0 && (
                    <Reveal>
                      {/* Right Arrow Drop from Crossbar (Desktop only) */}
                      <div className="hidden lg:flex flex-col items-center absolute w-full -top-[40px] left-0 pointer-events-none">
                        <div className="w-[3px] h-7 bg-slate-300" />
                        <ChevronDown className="text-slate-400 w-6 h-6 -mt-2 animate-bounce" />
                      </div>

                      <div className="bg-slate-50 border-2 border-slate-200 rounded-3xl p-6 shadow-sm h-full">
                        <div className="flex items-center justify-center gap-2 mb-6 text-emerald-700 font-mono text-xs font-bold uppercase tracking-widest">
                          <Wallet className="w-4 h-4" /> Treasury & Fiscal
                        </div>
                        <div className="flex flex-col gap-4">
                          {financeTeam.map((fin) => (
                            <Link
                              key={fin.slug || fin.name}
                              to={`/team/${fin.slug}`}
                              data-testid={`team-member-${fin.slug}`}
                              className="group flex flex-col xl:flex-row items-center gap-4 bg-white border border-slate-200 rounded-2xl p-4 hover:border-emerald-600 hover:shadow-md transition-all duration-300 text-center xl:text-left"
                            >
                              <div className="relative w-14 h-14 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center">
                                {fin.image ? (
                                  <img
                                    src={fin.image}
                                    alt={fin.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                ) : (
                                  <User className="w-6 h-6 text-slate-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-heading font-extrabold text-base text-michma-ink group-hover:text-emerald-700 transition-colors">
                                  {fin.name}
                                </h4>
                                <span className="text-[11px] font-bold text-emerald-700 block mt-0.5 uppercase tracking-wide">
                                  {fin.role}
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  )}
                </div>

              </div>
            )}

            {/* CONNECTING FLOW ARROW TO BOTTOM SECTIONS (CEO/Advisors) */}
            {(ceo || ceoTeam.length > 0 || advisors.length > 0) && (
              <div className="relative my-8 flex flex-col items-center justify-center">
                <div className="w-[3px] h-10 bg-slate-300" />
                <div className="w-10 h-10 rounded-full bg-slate-100 border-2 border-slate-300 flex items-center justify-center text-slate-600 shadow-md -mt-2 relative z-10">
                  <ChevronDown className="w-6 h-6 animate-bounce" />
                </div>
                <div className="w-full max-w-xl h-[3px] bg-slate-200 -mt-5 hidden md:block z-0" />
              </div>
            )}

            {/* ===================== TIER 4: STRATEGIC ADVISORY ===================== */}
            {advisors.length > 0 && (
              <div className="relative z-10 pt-4">
                <Reveal>
                  <div className="flex items-center justify-center gap-2 mb-8">
                    <Award className="w-6 h-6 text-amber-600" />
                    <span className="font-mono text-sm uppercase tracking-[0.2em] text-amber-600 font-extrabold">
                      Advisory Council
                    </span>
                  </div>
                </Reveal>

                <div className="max-w-md mx-auto">
                  {advisors.map((advisor, i) => (
                    <Reveal key={advisor.slug || i} delay={0.4}>
                      <Link
                        to={`/team/${advisor.slug}`}
                        data-testid={`team-member-${advisor.slug}`}
                        className="group relative block rounded-3xl bg-amber-50/50 border-2 border-amber-200 p-6 text-center hover:bg-white hover:shadow-xl transition-all duration-300"
                      >
                        <div className="relative w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden bg-amber-100 border-2 border-amber-300 flex items-center justify-center">
                          {advisor.image ? (
                            <img
                              src={advisor.image}
                              alt={advisor.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <User className="w-10 h-10 text-amber-600" />
                          )}
                        </div>
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-amber-700 block mb-1 font-bold">
                          {advisor.dept || "Advisory Board"}
                        </span>
                        <h4 className="font-heading font-extrabold text-2xl text-michma-ink group-hover:text-amber-600 transition-colors">
                          {advisor.name}
                        </h4>
                        <p className="text-sm font-extrabold text-amber-800 mt-2">
                          {advisor.role}
                        </p>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Empty Search Results State */
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 max-w-xl mx-auto shadow-xs">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-extrabold text-2xl text-michma-ink">
              No matching officers found
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              Try adjusting your search term to find active council members.
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-6 font-mono text-xs uppercase tracking-wider font-bold text-michma hover:underline cursor-pointer"
            >
              Reset Search
            </button>
          </div>
        )}
      </section>
    </div>
  );
}